import { InventoryListing } from "../models/inventory/InventoryListing";
import { InventoryFilters } from "../models/inventory/InventoryListingFilters";
import { InventorySwagpack } from "../models/inventory/InventorySwagpack";
import { SwagpackFilters } from "../models/inventory/InventorySwagpackFilter";
import { InventorySwagpackVariant } from "../models/inventory/InventorySwagpackVariant";
import {
  APIResponse,
  APIBlobResponse,
  PaginatedAPIResponse,
} from "../models/NetworkResponse";
import { getBlobAPI, getAPI, postMultipartAPI } from "./Networking";

export default class InventoryAPI {
  getIds = async (searchText: string): Promise<number[]> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("search_text", searchText);

      const response = await getAPI(
        `/v5/inventories/ids?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getOrderInventoryIdByOrderId = async (
    orderId: number,
  ): Promise<number | null> => {
    try {
      const response = await getAPI(
        `/v5/inventories/order/${orderId}/inventory-id`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getMasterInventoryIdByCustomerId = async (
    customerId: number,
  ): Promise<number | null> => {
    try {
      const response = await getAPI(
        `/v5/inventories/customer/${customerId}/inventory-id`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getListing = async (
    filters: InventoryFilters,
  ): Promise<PaginatedAPIResponse<InventoryListing> | APIBlobResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page_no)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit) queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text.toString());

      if (filters.order_id)
        queryParams.append("order_id", filters.order_id.toString());

      if (filters.customer_id)
        queryParams.append("customer_id", filters.customer_id.toString());

      if (filters.inventory_id)
        queryParams.append("inventory_id", filters.inventory_id.toString());

      if (
        filters.inventory_type !== null &&
        filters.inventory_type !== undefined
      ) {
        queryParams.append("inventory_type", filters.inventory_type.toString());
      }

      if (filters.sort_by)
        queryParams.append("sort_by", filters.sort_by.toString());

      if (filters.sort_type)
        queryParams.append("sort_type", filters.sort_type.toString());
      const response = await getAPI(
        `/v5/inventories?${queryParams.toString()}`,
      );

      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getSwagpacks = async (
    inventoryId: number,
    filters: SwagpackFilters,
  ): Promise<PaginatedAPIResponse<InventorySwagpack> | APIBlobResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.page_no)
        queryParams.append("page_no", filters.page_no.toString());

      if (filters.limit) queryParams.append("limit", filters.limit.toString());

      if (filters.search_text)
        queryParams.append("search_text", filters.search_text.toString());

      if (
        filters &&
        filters.out_of_stock !== null &&
        filters.out_of_stock !== undefined
      ) {
        queryParams.append("out_of_stock", filters.out_of_stock.toString());
      }

      if (filters.sort_by)
        queryParams.append("sort_by", filters.sort_by.toString());

      if (filters.sort_type)
        queryParams.append("sort_type", filters.sort_type.toString());

      const response = await getAPI(
        `/v5/inventories/inventory/${inventoryId}/packs?${queryParams.toString()}`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  getSwagpackVariants = async (
    packId: number,
  ): Promise<
    PaginatedAPIResponse<InventorySwagpackVariant> | APIBlobResponse
  > => {
    try {
      const response = await getAPI(
        `/v5/inventories/inventory/packs/${packId}/variants`,
      );
      return response.result;
    } catch (error) {
      throw error;
    }
  };

  downloadCountReportCsv = async (
    inventoryId: number,
  ): Promise<APIBlobResponse> => {
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      throw new Error("Invalid inventory ID");
    }

    try {
      const blob = await getBlobAPI(
        `/v1/inventory/${inventoryId}/count-report/csv`,
      );

      if (!blob) {
        throw new Error("Count report download failed");
      }

      return {
        data: blob,
        filename: `inventory-${inventoryId}-count-report.csv`,
      };
    } catch (error) {
      throw error;
    }
  };

  uploadSwagpackQuantityCsv = async (
    inventoryId: number,
    file: File,
  ): Promise<APIResponse> => {
    if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
      throw new Error("Invalid inventory ID");
    }

    try {
      const formData = new FormData();
      formData.append("csv_file", file);

      return await postMultipartAPI(
        `/v1/inventory/${inventoryId}/swagpack/quantity/import-csv`,
        formData,
      );
    } catch (error) {
      throw error;
    }
  };
}
